import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Types

export interface InventoryImportLine {
  binCode: string;
  zoneCode?: string;
  binTypeNo?: number;
  binGrpNo?: number;
  binCatNo?: number;
  description?: string;
  pickPriorityNo?: number;
  putAwayPriorityNo?: number;
  sharedBin?: boolean;
  itemNo?: string;
  quantity?: number;
}

export interface InventoryImportRequest {
  warehouseID: string;
  lines: InventoryImportLine[];
  balanceStrategy: 'replace' | 'add';
  dryRun: boolean;
}

export interface ImportValidationError {
  lineNumber: number;
  binCode?: string;
  itemNo?: string;
  message: string;
}

export interface CsvHeaderExtractionResponse {
  headers: string[];
  sampleRows: string[][];
  suggestedMappings: Record<string, string>;
  availableTargetFields: Record<string, string>;
  errorMessage?: string;
}

export type ColumnMappings = Record<string, string | null>;
export type DefaultValues = Record<string, string>;

export interface InventoryImportResponse {
  success: boolean;
  isDryRun: boolean;
  errorMessage?: string;
  processedAt: string;
  processedBy?: string;
  warehouseMode?: string;
  balanceStrategy?: string;
  locationsCreated: number;
  locationsUpdated: number;
  locationsSkipped: number;
  balancesCreated: number;
  balancesUpdated: number;
  balancesSkipped: number;
  auditBatchId?: string;
  auditTransactionsCreated: number;
  validationErrors: ImportValidationError[];
  warnings: string[];
  messages: string[];
}

class InventoryImportService {
  private baseUrl = `${API_BASE_URL}/inventory-import`;

  /**
   * Import locations and balances from JSON
   */
  async importJson(request: InventoryImportRequest): Promise<InventoryImportResponse> {
    const response = await AuthService.authenticatedPost(this.baseUrl, request);

    const data = await response.json();

    if (!response.ok && !data.validationErrors?.length) {
      throw new Error(extractApiError(data, response, 'Import feilet'));
    }

    return data;
  }

  /**
   * Extract CSV headers and sample data for column mapping UI
   */
  async extractCsvHeaders(
    file: File,
    delimiter: string = ';'
  ): Promise<CsvHeaderExtractionResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('delimiter', delimiter);

    const response = await AuthService.authenticatedFetch(`${this.baseUrl}/csv/headers`, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(extractApiError(data, response, 'Kunne ikke lese CSV-headers'));
    }

    return data;
  }

  /**
   * Import locations and balances from CSV file
   */
  async importCsv(
    file: File,
    warehouseId: string,
    balanceStrategy: 'replace' | 'add' = 'replace',
    dryRun: boolean = false,
    delimiter: string = ';',
    columnMappings?: ColumnMappings | null,
    defaultValues?: DefaultValues | null,
    skipInvalidLines: boolean = false
  ): Promise<InventoryImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('warehouseId', warehouseId);
    formData.append('balanceStrategy', balanceStrategy);
    formData.append('dryRun', dryRun.toString());
    formData.append('skipInvalidLines', skipInvalidLines.toString());
    formData.append('delimiter', delimiter);
    if (columnMappings) {
      formData.append('columnMappingsJson', JSON.stringify(columnMappings));
    }
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      formData.append('defaultValuesJson', JSON.stringify(defaultValues));
    }

    const response = await AuthService.authenticatedFetch(`${this.baseUrl}/csv`, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (!response.ok && !data.validationErrors?.length) {
      throw new Error(extractApiError(data, response, 'CSV-import feilet'));
    }

    return data;
  }

  /**
   * Validate CSV file without importing (dry run)
   */
  async validateCsv(
    file: File,
    warehouseId: string,
    balanceStrategy: 'replace' | 'add' = 'replace',
    delimiter: string = ';',
    columnMappings?: ColumnMappings | null,
    defaultValues?: DefaultValues | null,
    skipInvalidLines: boolean = false
  ): Promise<InventoryImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('warehouseId', warehouseId);
    formData.append('balanceStrategy', balanceStrategy);
    formData.append('skipInvalidLines', skipInvalidLines.toString());
    formData.append('delimiter', delimiter);
    if (columnMappings) {
      formData.append('columnMappingsJson', JSON.stringify(columnMappings));
    }
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      formData.append('defaultValuesJson', JSON.stringify(defaultValues));
    }

    const response = await AuthService.authenticatedFetch(`${this.baseUrl}/csv/validate`, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (!response.ok && !data.validationErrors?.length) {
      throw new Error(extractApiError(data, response, 'Validering feilet'));
    }

    return data;
  }
}

export const inventoryImportService = new InventoryImportService();
